/**
 * AbacProtectedRoute - Route component with ABAC-based access control
 *
 * Replaces the basic ProtectedRoute with full ABAC permission checking.
 * Redirects to login if not authenticated, shows AccessDenied if not authorized.
 */

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useAbacPermission } from "hooks/useAbacPermission";
import { loadUserContext } from "redux-store/abac/actions";
import { signOut } from "redux-store/auth/actions";
import { selectUserContext } from "redux-store/abac/selectors";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import LogoutIcon from "@mui/icons-material/Logout";

/**
 * Protected route with ABAC authorization
 */
const AbacProtectedRoute = ({
  component: Component = null,
  componentProps = null,
  resourceType = null,
  action = null,
  resourceIdParam = null,
  fallbackPath = null,
  loginPath = "/login",
  children = null,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [isInitialized, setIsInitialized] = useState(false);
  const userContext = useSelector(selectUserContext);
  const authToken = localStorage.getItem("authToken");

  // Extract resource ID from URL params if specified
  const getResourceId = () => {
    if (!resourceIdParam) return null;
    const params = new URLSearchParams(location.search);
    const pathParts = location.pathname.split("/");

    // Check URL params
    const paramValue = params.get(resourceIdParam);
    if (paramValue) return parseInt(paramValue, 10);

    // Check path for :id pattern
    const idIndex = pathParts.findIndex((part) => !isNaN(parseInt(part, 10)));
    if (idIndex !== -1) return parseInt(pathParts[idIndex], 10);

    return null;
  };

  const resourceId = getResourceId();

  // Only check permission if resourceType and action are specified
  const shouldCheckPermission = resourceType && action;
  const {
    granted,
    loading: permissionLoading,
    error: permissionError,
    refresh: retryPermission,
  } = useAbacPermission(
    resourceType || "",
    action || "",
    resourceId,
    { autoEvaluate: shouldCheckPermission }
  );

  // Check authentication
  useEffect(() => {
    if (!authToken) {
      navigate(loginPath, { replace: true, state: { from: location } });
      return;
    }

    // Load user context if not loaded
    if (!userContext && authToken) {
      dispatch(loadUserContext()).then(() => {
        setIsInitialized(true);
      });
    } else {
      setIsInitialized(true);
    }
  }, [authToken, userContext, dispatch, navigate, location, loginPath]);

  // Handle authorization after initialization
  useEffect(() => {
    if (!isInitialized || !shouldCheckPermission) return;
    if (permissionLoading || granted === null) return; // Wait for permission to be evaluated

    // A failed check (timeout, network, 5xx) is not a denial: it gets a Retry
    // below rather than the fallback redirect.
    if (granted === false && !permissionError) {
      if (fallbackPath) {
        navigate(fallbackPath, { replace: true });
      }
      // If no fallback, render access denied UI
    }
  }, [
    isInitialized,
    shouldCheckPermission,
    permissionLoading,
    granted,
    permissionError,
    fallbackPath,
    navigate,
  ]);

  // Show loading while checking - includes when permission evaluation is pending (granted === null)
  if (!isInitialized || (shouldCheckPermission && (permissionLoading || granted === null))) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Not authenticated - redirect handled in effect
  if (!authToken) {
    return null;
  }

  // The check itself failed (the evaluate call timed out or errored). The hook
  // fails closed with granted=false, but showing "Access Denied" for that
  // would be wrong and the old endless spinner was worse: offer a Retry.
  if (shouldCheckPermission && permissionError) {
    return (
      <PermissionCheckFailedContent
        onRetry={() => {
          retryPermission().catch(() => {
            // The hook records the error; this view stays up for another try.
          });
        }}
        onGoBack={() => navigate(-1)}
      />
    );
  }

  // If permission check enabled and explicitly denied (not just null/pending), show access denied
  // Note: granted === null means permission hasn't been evaluated yet
  if (shouldCheckPermission && granted === false) {
    return (
      <AccessDeniedContent
        resourceType={resourceType}
        action={action}
        onGoBack={() => navigate(-1)}
        onGoHome={() => navigate("/")}
        onLogout={() => dispatch(signOut())}
      />
    );
  }

  // Render the protected component
  if (Component) {
    return <Component {...(componentProps || {})} />;
  }

  return children || null;
};

AbacProtectedRoute.propTypes = {
  /** The component to render when authorized */
  component: PropTypes.elementType,
  /** Resource type for ABAC check (optional - skip ABAC if not provided) */
  resourceType: PropTypes.string,
  /** Action to check (optional - skip ABAC if not provided) */
  action: PropTypes.string,
  /** URL parameter name containing resource ID */
  resourceIdParam: PropTypes.string,
  /** Path to redirect on denial */
  fallbackPath: PropTypes.string,
  /** Path to redirect for login */
  loginPath: PropTypes.string,
  /** Alternative to component prop */
  children: PropTypes.node,
};

/**
 * Shown when the permission check could not be completed (as opposed to
 * completing with a denial).
 */
const PermissionCheckFailedContent = ({ onRetry, onGoBack }) => (
  <Box
    display="flex"
    flexDirection="column"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
    textAlign="center"
    p={4}
  >
    <Box component="h2" sx={{ fontSize: "1.5rem", fontWeight: "medium", mb: 2 }}>
      We couldn&apos;t check your access to this page
    </Box>
    <Box component="p" sx={{ color: "text.secondary", mb: 4, maxWidth: 420 }}>
      The permission check didn&apos;t respond in time or failed. This is usually temporary.
    </Box>
    <Box display="flex" gap={2} flexWrap="wrap" justifyContent="center">
      <Box
        component="button"
        onClick={onRetry}
        sx={{
          px: 4,
          py: 1.5,
          borderRadius: 1,
          border: "none",
          bgcolor: "primary.main",
          color: "primary.contrastText",
          cursor: "pointer",
          "&:hover": { bgcolor: "primary.dark" },
        }}
      >
        Retry
      </Box>
      <Box
        component="button"
        onClick={onGoBack}
        sx={{
          px: 4,
          py: 1.5,
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
          background: "transparent",
          cursor: "pointer",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        Go Back
      </Box>
    </Box>
  </Box>
);

PermissionCheckFailedContent.propTypes = {
  onRetry: PropTypes.func.isRequired,
  onGoBack: PropTypes.func.isRequired,
};

/**
 * Access denied content component
 */
const AccessDeniedContent = ({ resourceType, action, onGoBack, onGoHome, onLogout }) => (
  <Box
    display="flex"
    flexDirection="column"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
    textAlign="center"
    p={4}
  >
    <Box
      component="h1"
      sx={{
        fontSize: "6rem",
        fontWeight: "bold",
        color: "error.main",
        mb: 2,
      }}
    >
      403
    </Box>
    <Box
      component="h2"
      sx={{
        fontSize: "1.5rem",
        fontWeight: "medium",
        mb: 2,
      }}
    >
      Access Denied
    </Box>
    <Box
      component="p"
      sx={{
        color: "text.secondary",
        mb: 4,
        maxWidth: 400,
      }}
    >
      {resourceType && action
        ? `You don't have permission to ${action} this ${resourceType}.`
        : "You don't have permission to access this page."}
    </Box>
    {/* Wraps on narrow screens: the three buttons are ~410px wide, so at 390px they
        pushed the page into horizontal scroll (conformance TC-NFR-002). */}
    <Box display="flex" gap={2} flexWrap="wrap" justifyContent="center">
      <Box
        component="button"
        onClick={onGoBack}
        sx={{
          px: 4,
          py: 1.5,
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
          background: "transparent",
          cursor: "pointer",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        Go Back
      </Box>
      <Box
        component="button"
        onClick={onGoHome}
        sx={{
          px: 4,
          py: 1.5,
          borderRadius: 1,
          border: "none",
          bgcolor: "primary.main",
          color: "primary.contrastText",
          cursor: "pointer",
          "&:hover": { bgcolor: "primary.dark" },
        }}
      >
        Go to Dashboard
      </Box>
      <Box
        component={Link}
        to="/login"
        onClick={onLogout}
        sx={{
          px: 4,
          py: 1.5,
          borderRadius: 1,
          border: "none",
          background: "transparent",
          color: "error.main",
          cursor: "pointer",
          textDecoration: "none",
          display: "flex",
          alignItems: "center",
          gap: 1,
          "&:hover": { bgcolor: "error.light", color: "error.dark" },
        }}
      >
        <LogoutIcon fontSize="small" />
        Logout
      </Box>
    </Box>
  </Box>
);

AccessDeniedContent.propTypes = {
  resourceType: PropTypes.string,
  action: PropTypes.string,
  onGoBack: PropTypes.func.isRequired,
  onGoHome: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
};

export default AbacProtectedRoute;
